// "Someone got there first": while you type a reply, the client writes again or a
// teammate answers, and your reply is out of date before you send it.
//
// The composer takes a BASELINE (the ids of the messages on the ticket when the draft
// began). Anything on the ticket now that is not in the baseline, not already
// acknowledged and not yours is a collision. A teammate's reply is named from the
// "replied_outbound" event written for it; a reply is yours when that event's actor is
// you (another tab or device) or when its idempotency key is one this browser sent.
//
// Pure on purpose: tests/draft-collision.test.ts.

export interface CollisionMessage {
  id: string;
  direction: string;
  created_at: string;
  idempotency_key?: string | null;
}

export interface CollisionEvent {
  event_type: string;
  actor: string | null;
  created_at: string;
}

export type Collision =
  | { id: string; kind: 'client'; at: string }
  | { id: string; kind: 'teammate'; at: string; actorSlackId: string | null };

/** How close a reply event must be to a message to be taken as its author. */
const EVENT_WINDOW_MS = 2 * 60_000;

function authorOf(m: CollisionMessage, events: readonly CollisionEvent[]): string | null | undefined {
  const at = Date.parse(m.created_at);
  let best: CollisionEvent | null = null;
  let bestGap = Infinity;
  for (const e of events) {
    if (e.event_type !== 'replied_outbound') continue;
    const gap = Math.abs(Date.parse(e.created_at) - at);
    if (gap <= EVENT_WINDOW_MS && gap < bestGap) {
      best = e;
      bestGap = gap;
    }
  }
  // undefined: no event found (unknown author); null/string: the event's actor
  return best ? best.actor : undefined;
}

export function findCollisions(input: {
  messages: readonly CollisionMessage[];
  /** Message ids when the draft began; null while there is no draft. */
  baseline: ReadonlySet<string> | null;
  events: readonly CollisionEvent[];
  mySlackId: string | null;
  /** Idempotency keys of replies sent from this browser. */
  mySentKeys: ReadonlySet<string>;
  acknowledged?: ReadonlySet<string>;
}): Collision[] {
  const { messages, baseline, events, mySlackId, mySentKeys, acknowledged } = input;
  if (!baseline) return [];
  const out: Collision[] = [];
  for (const m of messages) {
    if (baseline.has(m.id)) continue;
    const id = 'msg:' + m.id;
    if (acknowledged?.has(id)) continue;
    if (m.direction === 'inbound') {
      out.push({ id, kind: 'client', at: m.created_at });
    } else if (m.direction === 'outbound') {
      if (m.idempotency_key && mySentKeys.has(m.idempotency_key)) continue;
      const author = authorOf(m, events);
      if (mySlackId && author === mySlackId) continue;
      out.push({ id, kind: 'teammate', at: m.created_at, actorSlackId: author ?? null });
    }
  }
  return out.sort((a, b) => a.at.localeCompare(b.at));
}

/** One sentence for the banner: "Kurt wrote again. Liz has already replied." */
export function collisionSummary(
  collisions: readonly Collision[],
  names: ReadonlyMap<string, string>,
  clientName: string,
): string {
  const parts: string[] = [];
  const client = collisions.filter((c) => c.kind === 'client');
  if (client.length > 0) {
    const who = clientName.trim() || 'The client';
    parts.push(client.length === 1 ? `${who} wrote again.` : `${who} wrote again (${client.length} new messages).`);
  }
  const mates = collisions.filter((c): c is Extract<Collision, { kind: 'teammate' }> => c.kind === 'teammate');
  if (mates.length > 0) {
    const named: string[] = [];
    let unknown = false;
    for (const c of mates) {
      const n = c.actorSlackId ? names.get(c.actorSlackId) : undefined;
      if (!n) unknown = true;
      else if (!named.includes(n)) named.push(n);
    }
    if (named.length === 0 || unknown) {
      parts.push(named.length === 0 ? 'A teammate has already replied.' : `${named.join(' and ')} and a teammate have already replied.`);
    } else {
      parts.push(`${named.join(' and ')} ${named.length === 1 ? 'has' : 'have'} already replied.`);
    }
  }
  return parts.join(' ');
}
